import { DispatchBoardQueueLoad } from "app/data/city-delivery/dispatch-board/models";
import React, { memo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import Loading from "ui-components/loading/loading";
import CityDeliveryDispatchWorkflowProgress from "./progress/cityDeliveryDispatchWorkflowProgress";
import CityDeliveryDispatchWorkflowSection from "./cityDeliveryDispatchWorkflowSection";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import CityDeliveryDispatchWorkflowLoadSummary from "./load-summary/cityDeliveryDispatchWorkflowLoadSummary";
import CityDeliveryDispatchWorkflowActions from "./actions/cityDeliveryDispatchWorkflowActions";
import CityDeliveryDispatchWorkflowTools from "./tools/cityDeliveryDispatchWorkflowTools";
import CityDeliveryDispatchWorkflowLoadDetails from "./load-details/cityDeliveryDispatchWorkflowLoadDetails";
import { useGetDispatchBoardQueueLoad } from "../hooks/useDispatchBoardQueueLoad";
import { userSelector } from "slices/user/userSlice";

export interface CityDeliveryDispatchWorkflowProps {
    dispatchLoad: DispatchBoardQueueLoad;
    onClose: () => void;
    onLoadManifestNumberUpdate?: (manifestNumber: number) => void;
}

const CityDeliveryDispatchWorkflow: React.FC<CityDeliveryDispatchWorkflowProps> = memo((props) => {
    const { dispatchLoad, onClose, onLoadManifestNumberUpdate } = props;
    const workflowSummaryRef = useRef<HTMLDivElement>(null);
    const workflowDetailsRef = useRef<HTMLDivElement>(null);
    const terminalNumber = useSelector(userSelector)?.activeTerminal?.id;

    const {
        dispatchLoad: fetchedLoad,
        isLoadingDispatchLoad,
        isFetchingDispatchLoad,
        isErrorDispatchLoad,
        errorDispatchLoad,
        isFetchedDispatchLoad,
    } = useGetDispatchBoardQueueLoad({
        terminalNumber,
        picklistNumber: dispatchLoad.picklistNumber,
        manifestNumber: dispatchLoad.manifestNumber,
    });

    useEffect(() => {
        if (!isErrorDispatchLoad) return;
        const msg =
            (errorDispatchLoad as any)?.getError?.() ??
            (errorDispatchLoad as any)?.response?.data?.detail ??
            (errorDispatchLoad as any)?.data?.detail ??
            "Failed to load load details";
        toast.error(msg);
        onClose();
    }, [isErrorDispatchLoad, errorDispatchLoad, onClose]);

    useEffect(() => {
        if (!isFetchedDispatchLoad || isLoadingDispatchLoad || isFetchingDispatchLoad || isErrorDispatchLoad) return;
        if (!fetchedLoad) {
            toast.error("Load not found");
            onClose();
        }
    }, [isFetchedDispatchLoad, isLoadingDispatchLoad, isFetchingDispatchLoad, isErrorDispatchLoad, fetchedLoad, onClose]);

    const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const showLoading = isLoadingDispatchLoad || (isFetchingDispatchLoad && !fetchedLoad);
    const showContent = fetchedLoad && !showLoading;

    return (
        <SlideOutSidebar
            header="Load Details"
            onClose={onClose}
            size="medium"
            show={true}
        >
            {showLoading && (
                <div className="city-delivery-dispatch-workflow city-delivery-dispatch-workflow--loading">
                    <Loading isLoading={true} />
                    <span className="city-delivery-dispatch-workflow__loading-text">Loading load details…</span>
                </div>
            )}
            {showContent && (
            <div className="city-delivery-dispatch-workflow">
                <CityDeliveryDispatchWorkflowSection
                    titleText="Load Summary"
                    titleAction={<>View Details <XGSIcon icon={XGSIcons.faAngleDown} /></>}
                    onTitleActionClick={() => scrollToSection(workflowDetailsRef)}
                    ref={workflowSummaryRef}
                    content={<CityDeliveryDispatchWorkflowLoadSummary
                        picklistNumber={fetchedLoad.picklistNumber}
                        manifestNumber={fetchedLoad.manifestNumber}
                        status={fetchedLoad.status}
                        routeName={fetchedLoad.routeName}
                        door={fetchedLoad.door}
                        stops={fetchedLoad.stops}
                        probills={fetchedLoad.probills}
                        pieces={fetchedLoad.pieces}
                        weight={fetchedLoad.weight}
                    />}
                />

                <CityDeliveryDispatchWorkflowSection
                    titleText="Workflow Progress"
                    content={<CityDeliveryDispatchWorkflowProgress
                        dispatchLoadStatus={fetchedLoad.status}
                    />}
                />

                <CityDeliveryDispatchWorkflowSection
                    titleText=""
                    content={<CityDeliveryDispatchWorkflowActions
                        dispatchLoad={fetchedLoad}
                        onLoadManifestNumberUpdate={onLoadManifestNumberUpdate}
                    />}
                />

                <CityDeliveryDispatchWorkflowSection
                    titleText=""
                    content={<CityDeliveryDispatchWorkflowTools
                        dispatchLoad={fetchedLoad}
                        terminalNumber={terminalNumber}
                    />}
                />

                <CityDeliveryDispatchWorkflowSection
                    titleText="Load Details"
                    titleAction={<><XGSIcon icon={XGSIcons.faAngleUp} /> Back to Summary</>}
                    onTitleActionClick={() => scrollToSection(workflowSummaryRef)}
                    ref={workflowDetailsRef}
                    content={<CityDeliveryDispatchWorkflowLoadDetails
                        terminalNumber={terminalNumber}
                        picklistNumber={fetchedLoad.picklistNumber}
                        manifestNumber={fetchedLoad.manifestNumber}
                        dispatchLoadStatus={fetchedLoad.status}
                    />}
                />
            </div>
            )}
        </SlideOutSidebar>
    );
});

export default CityDeliveryDispatchWorkflow;
